import PropTypes from 'prop-types';
import React from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import { FIND_COUNTRIES_WITH_CITIES } from 'constants/findJobs';
import postedAgo from 'helpers/postedAgo';
import config from '../../config';
import getJobsCanonical from '../Projects/getJobsCanonical';
import { groupRolesByArea } from './groupRolesByArea';
import styles from './JobCategories.module.scss';

const TITLE = 'Browse freelance jobs by role | Twine';
const DESCRIPTION =
  'Browse freelance jobs by role and country. Explore design, video, development, marketing, and music roles, or jump into the latest openings on Twine.';

/** Brief title lives on `text` (API/entity); accept `title` for fixtures. */
function jobTitle(job) {
  return (job && (job.text || job.title)) || '';
}

function areaAboutLabel(heading) {
  return `About ${heading.toLowerCase()} work on Twine`;
}

const JobCategoriesBase = ({
  roles = [],
  latestJobs = [],
  location = null,
}) => {
  const { areas, unplaced } = groupRolesByArea(roles);
  const { canonical } = getJobsCanonical({
    categories: true,
    query: (location && location.search) || '',
    baseUrl: config.baseUrl,
  });

  if (unplaced.length > 0 && typeof console !== 'undefined') {
    // Flag roles missing from the area map so the list cannot silently drift
    // eslint-disable-next-line no-console
    console.warn(
      '[JobCategories] roles with no area mapping:',
      unplaced.map((r) => r.name)
    );
  }

  return (
    <div className={styles.page} data-testid="job-categories-page">
      <Helmet
        title={TITLE}
        link={[{ rel: 'canonical', href: canonical }]}
        meta={[
          { name: 'description', content: DESCRIPTION },
          { property: 'og:title', content: TITLE },
          { property: 'og:description', content: DESCRIPTION },
          { property: 'og:url', content: canonical },
          { name: 'twitter:title', content: TITLE },
          { name: 'twitter:description', content: DESCRIPTION },
        ]}
      />

      <div className={styles.inner}>
        <Breadcrumbs className={styles.breadcrumbs}>
          <Link to="/jobs">Jobs</Link>
          <span>All job categories</span>
        </Breadcrumbs>

        <h1 className={styles.title}>Browse freelance jobs by role</h1>
        <p className={styles.intro}>
          Explore freelance and remote jobs across every creative role on Twine.
          Pick a specialty below, browse openings by country, or jump into the
          latest jobs posted by clients worldwide.
        </p>

        <section className={styles.section} aria-labelledby="roles-by-area">
          <h2 id="roles-by-area" className={styles.sectionHeading}>
            Jobs by role
          </h2>
          {areas.map((area) => (
            <div key={area.id} className={styles.areaBlock}>
              <h3 className={styles.areaHeading}>
                <a href={area.href}>{area.heading}</a>
              </h3>
              <ul className={styles.linkList}>
                {area.roles.map((role) => (
                  <li key={role.name}>
                    <a href={role.href}>{`${role.name} jobs`}</a>
                  </li>
                ))}
              </ul>
              <a href={area.href} className={styles.aboutLink}>
                {areaAboutLabel(area.heading)}
              </a>
            </div>
          ))}
          {unplaced.length > 0 && (
            <div
              className={styles.unplacedFlag}
              data-testid="unplaced-roles"
              data-unplaced={unplaced.map((r) => r.name).join(',')}
            />
          )}
        </section>

        <section className={styles.section} aria-labelledby="jobs-by-country">
          <h2 id="jobs-by-country" className={styles.sectionHeading}>
            Jobs by country
          </h2>
          {FIND_COUNTRIES_WITH_CITIES.map((country) => (
            <div key={country.slug} className={styles.areaBlock}>
              <h3 className={styles.areaHeading}>
                <a href={`/jobs/in/${country.slug}`}>
                  {`Jobs in ${country.name}`}
                </a>
              </h3>
              <ul className={styles.linkList}>
                {country.cities.map((city) => (
                  <li key={city.slug}>
                    <a href={`/jobs/in/${country.slug}/${city.slug}`}>
                      {`Jobs in ${city.label}`}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <a href="/jobs" className={styles.aboutLink}>
            Jobs worldwide
          </a>
        </section>

        {latestJobs.length > 0 && (
          <section className={styles.section} aria-labelledby="latest-jobs">
            <h2 id="latest-jobs" className={styles.sectionHeading}>
              Latest jobs
            </h2>
            <ul className={styles.latestJobsList}>
              {latestJobs.map((job) => (
                <li key={job.id}>
                  <a href={job.links && job.links.main_relative}>
                    {jobTitle(job)}
                  </a>
                  {job.approved_at && (
                    <span className={styles.postedMeta}>
                      {postedAgo(job.approved_at)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <a href="/jobs" className={styles.seeAll}>
              See all jobs
            </a>
          </section>
        )}
      </div>
    </div>
  );
};

JobCategoriesBase.propTypes = {
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug_plural: PropTypes.string,
      category: PropTypes.string,
    })
  ),
  latestJobs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string,
      title: PropTypes.string,
      approved_at: PropTypes.string,
      links: PropTypes.shape({
        main_relative: PropTypes.string,
      }),
    })
  ),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

JobCategoriesBase.defaultProps = {
  location: null,
};

export default JobCategoriesBase;
